import { SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Conversion, ICampaignAttributes, IEvent, IFlight } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { Button, Checkbox, Label, Popover, Toolbar, Tooltip } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';
import type { DisplayModel } from '@feathr/rachis';

export interface IConversionTableCardToolbarProps {
  activeTab: 'conversions' | 'goals';
  localUrl: ((url: string) => string) | undefined;
  model: DisplayModel<ICampaignAttributes | IFlight | IEvent>;
  toggleExportModal: () => void;
  toggleRerunModal: () => void;
  columnIds: string[];
  updateColumnIds: (columnIds: string[]) => void;
  columns: Array<IColumn<Conversion>>;
}

function ConversionTableCardToolbar({
  activeTab,
  columns,
  columnIds,
  localUrl,
  model,
  toggleExportModal,
  toggleRerunModal,
  updateColumnIds,
}: Readonly<IConversionTableCardToolbarProps>): JSX.Element {
  const { t } = useTranslation();
  const { spacing } = useMantineTheme();

  const getColumnSetter = (columnId: string) => {
    return (): void => {
      const newColumnIds = [...columnIds];
      const index = newColumnIds.indexOf(columnId);
      const columnVisible = index >= 0;
      if (columnVisible) {
        newColumnIds.splice(index, 1);
      } else {
        newColumnIds.push(columnId);
      }
      updateColumnIds(newColumnIds);
    };
  };

  const isRecalculating = model.get('rerun', false);
  const RecalculateButton = (
    <Button
      disabled={isRecalculating}
      onClick={toggleRerunModal}
      prefix={getIconForAction('recalculate')}
      type={'secondary'}
    >
      {t('Recalculate')}
    </Button>
  );

  return (
    <Toolbar>
      {activeTab === 'conversions' && (
        <Tooltip title={t('Customize columns')}>
          <Popover closeOnBlur={false}>
            <Button prefix={getIconForAction('customize')} type={'secondary'}>
              {t('Customize columns')}
            </Button>
            <Stack gap={spacing[2]} p={spacing[2]}>
              <Label>{t('Columns')}</Label>
              <SimpleGrid cols={2} spacing={spacing[3]} verticalSpacing={spacing[2]}>
                {columns.map((column) => {
                  return (
                    <Checkbox
                      id={`checkbox-${column.id}`}
                      key={column.id}
                      label={typeof column.checkboxLabel !== 'function' && column.checkboxLabel}
                      name={column.id}
                      onChange={getColumnSetter(column.id!)}
                      value={columnIds.includes(column.id!)}
                    />
                  );
                })}
              </SimpleGrid>
            </Stack>
          </Popover>
        </Tooltip>
      )}
      {activeTab === 'conversions' && (
        <Button onClick={toggleExportModal} prefix={getIconForAction('export')} type={'secondary'}>
          {t('Export to CSV')}
        </Button>
      )}
      {localUrl &&
        (isRecalculating ? (
          <Tooltip title={t('Conversions are currently being recalculated. Check back later.')}>
            {RecalculateButton}
          </Tooltip>
        ) : (
          <>{RecalculateButton}</>
        ))}
    </Toolbar>
  );
}

export default observer(ConversionTableCardToolbar);
